import { VgApiResult, vgApiRequest } from "./vg-api.service";

/**
 * Appointment field names follow the Core contracts (IAppointmentOptions,
 * DC_Create_Org_Appointment): the times are plain `start` / `end` strings, not
 * `startDateTime_UTC` / `endDateTime_UTC`. This file previously used the latter,
 * which the API silently ignored - so every appointment was written with no
 * times at all - and it read from Read_All_Service_Orders_By_Org_ID, a different
 * table, so created appointments never appeared in the list.
 */
export type AppointmentComplete = {
  appointmentId: number;
  appointmentTitle: string;
  appointmentType: number;
  start?: string;
  end?: string;
  /**
   * Derived, not stored - see ALL_DAY note below. True when the appointment runs
   * midnight to midnight UTC.
   */
  allDay: boolean;
  durationMilliseconds?: number;
  contactId?: number;
  timeZoneIANA?: string;
};

const MS_PER_DAY = 86400000;

/**
 * All-day appointments.
 *
 * Core has no all-day column and the create contract has no flag, so an all-day
 * appointment is encoded as one running from exactly UTC midnight to exactly UTC
 * midnight - the iCalendar DATE versus DATE-TIME distinction.
 *
 * The read side does not re-derive that here: the API computes `allDay` on the
 * way out (CoreObjects.AppointmentTimes.IsAllDay), so every client applies the
 * same rule rather than each keeping its own copy. This file only has to encode
 * it correctly on the way in.
 */

/**
 * UTC midnight of the calendar date the caller meant.
 *
 * Date-only strings are already UTC midnight per the ECMAScript date-time string
 * format, so they pass straight through. A local datetime is reduced to its
 * *local* calendar date first, because that is the date the user selected on
 * screen - reading UTC components off it would move the date across midnight.
 */
function toUtcMidnightIso(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00.000Z`;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}T00:00:00.000Z`;
}

type AppointmentsResponse = {
  listOfAppointments?: Array<Record<string, unknown>>;
  ListOfAppointments?: Array<Record<string, unknown>>;
};

function pickAppointment(item: Record<string, unknown>): AppointmentComplete {
  return {
    appointmentId: Number(item.appointmentId ?? item.AppointmentId ?? 0),
    appointmentTitle: String(item.appointmentTitle ?? item.AppointmentTitle ?? ""),
    appointmentType: Number(item.appointmentType ?? item.AppointmentType ?? 0),
    start: item.start != null ? String(item.start) : undefined,
    end: item.end != null ? String(item.end) : undefined,
    allDay: Boolean(item.allDay ?? item.AllDay ?? false),
    durationMilliseconds:
      item.durationMilliseconds != null
        ? Number(item.durationMilliseconds)
        : undefined,
    contactId: item.contactId != null ? Number(item.contactId) : undefined,
    timeZoneIANA:
      item.timeZoneIANA != null ? String(item.timeZoneIANA) : undefined,
  };
}

/**
 * The API takes the duration alongside start/end rather than deriving it, so
 * compute it here. Returns 0 when either end is missing or unparseable, which
 * is what the contract's default would have been anyway.
 */
function durationMillisecondsBetween(start?: string, end?: string): number {
  if (!start || !end) return 0;
  const from = new Date(start).getTime();
  const to = new Date(end).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return 0;
  return to - from;
}

/**
 * Normalises a datetime to a UTC ISO string, because the API stores these in a
 * `datetime2` column, which has no offset, and reads them back with
 * `startDate.ToUniversalTime()`.
 *
 * That read runs against a DateTime with Kind=Unspecified, so .NET treats the
 * stored value as *server* local time. The API servers run UTC, which makes the
 * conversion a no-op - and therefore makes UTC the only input that survives the
 * round trip.
 *
 * The two wrong answers, both confirmed against SQL Server:
 *   "2026-08-14T10:00:00"       stored as 10:00, read back as 10:00 UTC, so a
 *                               browser in BST renders it at 11:00.
 *   "2026-08-14T10:00:00+01:00" the offset is silently dropped on conversion to
 *                               datetime2, giving the same one-hour error.
 *
 * Passing an already-UTC string through is a no-op, so this is safe to apply
 * to any caller's value.
 */
function toUtcIsoString(value?: string): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toISOString();
}

/** The browser's IANA zone, e.g. "Europe/London". Falls back to UTC. */
function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export async function readOrgAppointments(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<AppointmentComplete[]>> {
  const { data, error } = await vgApiRequest<AppointmentsResponse>(
    accessToken,
    "Read_All_Org_Appointments_By_Org_ID",
    { orgId }
  );
  if (error) return { data: null, error };
  const list = data?.listOfAppointments ?? data?.ListOfAppointments ?? [];
  return { data: list.map(pickAppointment), error: null };
}

export type AppointmentInput = {
  orgId: number;
  appointmentTitle: string;
  appointmentType: number;
  start?: string;
  end?: string;
  allDay?: boolean;
  contactId?: number;
};

/**
 * Resolves a caller's start/end into the wire values, applying the all-day
 * encoding when asked for. An all-day end is exclusive - matching FullCalendar -
 * and defaults to a single day.
 */
function resolveTimes(payload: AppointmentInput): {
  start: string;
  end: string;
  durationMilliseconds: number;
} {
  if (!payload.allDay) {
    return {
      start: toUtcIsoString(payload.start),
      end: toUtcIsoString(payload.end),
      durationMilliseconds: durationMillisecondsBetween(payload.start, payload.end),
    };
  }

  if (!payload.start) {
    return { start: "", end: "", durationMilliseconds: 0 };
  }

  const start = toUtcMidnightIso(payload.start);
  const end = payload.end
    ? toUtcMidnightIso(payload.end)
    : new Date(new Date(start).getTime() + MS_PER_DAY).toISOString();

  return {
    start,
    end,
    durationMilliseconds: Math.max(
      0,
      new Date(end).getTime() - new Date(start).getTime()
    ),
  };
}

export async function createAppointment(
  accessToken: string,
  payload: AppointmentInput
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  return vgApiRequest(accessToken, "Create_Appointment", {
    orgId: payload.orgId,
    appointmentTitle: payload.appointmentTitle,
    appointmentType: payload.appointmentType,
    ...resolveTimes(payload),
    contactId: payload.contactId ?? 0,
    timeZoneIANA: localTimeZone(),
  });
}

export async function updateAppointment(
  accessToken: string,
  payload: AppointmentInput & { appointmentId: number }
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Update_Appointment", {
    orgId: payload.orgId,
    appointmentId: payload.appointmentId,
    appointmentTitle: payload.appointmentTitle,
    appointmentType: payload.appointmentType,
    ...resolveTimes(payload),
    contactId: payload.contactId ?? 0,
    timeZoneIANA: localTimeZone(),
  });
}

export async function deleteAppointment(
  accessToken: string,
  orgId: number,
  appointmentId: number
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Delete_Appointment", { orgId, appointmentId });
}
